import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  affectsGeometry,
  planEditKey,
  planEditsSignature,
  type EffectivePlan,
  type PlanEdit,
  type RoomPlanCapture,
} from "@sts/geometry";
import { ApiError, ApiUnreachableError, type ApiClient } from "../api/client.js";
import type { PlanEditBatch, PlanEditList, PlanEditRecord, PlanEditVersionConflict } from "../api/types.js";
import {
  conflictMessage,
  editNaming,
  otherElements,
  versionConflicts,
  type ConflictMessageOptions,
} from "./plan-conflicts.js";
import { heatLossWordsOf, type HeatLossWords } from "./heat-loss-words.jsx";

/**
 * The plan's edit layer, held for the page and written through to the API
 * (migration 0039).
 *
 * The records are corrections to the scan: one per wall, opening or room
 * (`@sts/geometry`'s `PlanEdit`). What the page draws is
 * `applyPlanEdits(capture, records)` — the capture itself is never changed —
 * so this hook holds only the records. It lives at the survey page, not in the
 * plan, because the heat loss section needs to know the plan has been edited
 * as much as the drawing does.
 *
 * OPTIMISTIC, IN ORDER. A change shows on the plan at once. Changes are sent
 * one at a time, in the order they were made, each as one all-or-nothing batch
 * (a wall moved with the corners it shares is one change). What is shown is
 * the server's last answer with every change still in flight laid over it, so
 * a reply to an earlier change never briefly undoes a later one. A change the
 * server refuses is dropped from the drawing and the reason is shown in words.
 *
 * THE HEAT LOSS FOLLOWS. Saving a correction that moves a wall or opening
 * re-converts the survey's heat-loss rooms on the server, in the same request
 * (apps/api/src/survey/plan-edit-heat-loss.ts). Each answer says where the
 * heat loss now is (`heatLossSignature`) and what the save did to it: a
 * re-conversion counts in `recalculations`, which the Survey page watches to
 * re-read its figures once the queue has drained. The rooms are merged: what
 * a person entered or confirmed in the survey is never overwritten, and when
 * the answer says something was kept (or the heat loss could not follow at
 * all), its plain sentence and anything to check are held in `heatLossNote`
 * until the next change or until dismissed.
 *
 * TWO PEOPLE, ONE PLAN (migration 0046). Every correction has a version, and
 * each change is sent with the version of every element it touches that the
 * change was made on — 0 for "no correction held". When a colleague (or this
 * person in another tab) has changed one of them since, the server refuses
 * the whole change: the page then shows the server's version of those
 * elements, says who changed what and when (`plan-conflicts.ts`), drops any
 * later queued change built on the refused one, and re-reads the list. It
 * never re-sends the refused change over theirs. The version a change is
 * made on is the one this page last saw from the server — not one that
 * arrived while the change was still queued, which the person had not seen
 * when they made it. An undo or redo is made on what THIS page last saved,
 * so undoing never quietly throws away a colleague's change made since.
 */

export interface PlanEditsSource {
  /** `null`: this API has no edit layer (predates migration 0039). */
  list(): Promise<PlanEditList | null>;
  /** `baseVersions`, by `planEditKey`: the version of each element the change was made on. */
  batch(batch: PlanEditBatch, baseVersions?: ReadonlyMap<string, number>): Promise<PlanEditList>;
}

export function planEditsFromApi(client: ApiClient, surveyId: string): PlanEditsSource {
  return {
    list: () => client.planEdits(surveyId),
    batch: (batch, baseVersions) => client.planEditBatch(surveyId, batch, baseVersions),
  };
}

export interface PlanEditCommitOptions {
  /**
   * An undo or redo: made on what this page last saved of each element, so a
   * colleague's change since is refused rather than undone.
   */
  readonly replay?: boolean;
}

export type PlanEditsStatus = "unavailable" | "loading" | "ready" | "failed" | "unsupported";

/** A record as the page holds it: saved (with who and when), or still on its way. */
export type HeldPlanEdit = PlanEditRecord & { readonly pending?: boolean };

export interface PlanEditsState {
  readonly status: PlanEditsStatus;
  readonly records: readonly HeldPlanEdit[];
  /** `planEditsSignature(records)` — "" for the scan as captured. */
  readonly signature: string;
  /** A change is on its way to the server (or queued behind one that is). */
  readonly saving: boolean;
  /**
   * The signature the survey's heat loss was calculated at, as the server
   * last said — `HEAT_LOSS_BASIS_SIGNATURE` from an API too old to say.
   */
  readonly heatLossSignature: string;
  /** How many saves, this session, re-converted the heat-loss rooms. */
  readonly recalculations: number;
  /**
   * What the last save did to the heat loss when there is something to say —
   * a person's values kept, with anything to check, or why it could not
   * follow — until the next change or dismissal.
   */
  readonly heatLossNote: HeatLossWords | null;
  readonly dismissHeatLossNote: () => void;
  readonly error: string | null;
  readonly loadError: string | null;
  /** Sends one change. Resolves true once saved, false if it was refused (and undone). */
  readonly commit: (batch: PlanEditBatch, options?: PlanEditCommitOptions) => Promise<boolean>;
  readonly dismissError: () => void;
  readonly reload: () => void;
}

/**
 * What the floor plan needs to draw and edit the corrected plan: the capture
 * underneath, the records, the plan they make, and whether this person may
 * change it.
 */
export interface PlanEditLayer {
  readonly capture: RoomPlanCapture;
  readonly state: PlanEditsState;
  readonly plan: EffectivePlan;
  readonly canEdit: boolean;
}

/**
 * The geometry a survey's heat loss is calculated at when the API does not
 * say: the scan as captured, signature "". The API says — `heatLossSignature`
 * on the plan-edit list, the signature its scan conversion recorded — and
 * `PlanEditsState.heatLossSignature` carries that; this is the fallback for a
 * server that predates it, against which any wall correction reads as news.
 */
export const HEAT_LOSS_BASIS_SIGNATURE = "";

/**
 * What to say where the heat loss is shown when the plan has been corrected
 * since it was calculated — or null when it has not. Numbers are never
 * changed silently: the heat loss stays as calculated, and this says why the
 * plan and it may now disagree. Now that a saved correction re-converts the
 * rooms, this is only ever true where that could not happen (rooms a person
 * edited, a survey a newer one has replaced, rooms typed in rather than
 * scanned, corrections saved before the re-run existed) — see
 * `heatLossPlanNotice` for the page's question.
 */
export function planEditedNotice(
  records: readonly HeldPlanEdit[],
  basisSignature: string = HEAT_LOSS_BASIS_SIGNATURE,
): string | null {
  if (planEditsSignature(records) === basisSignature) return null;
  const geometry = records.filter(affectsGeometry);
  if (geometry.length === 0) return null;
  const latest = [...geometry].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))[0] as HeldPlanEdit;
  const who = latest.pending ? "" : latest.updatedBy?.name || latest.createdBy?.name || "";
  const date = editDate(latest.updatedAt) ?? "";
  // A room split or merged is geometry too (plan-edits.ts affectsGeometry).
  const onRooms = geometry.some((r) => r.targetKind === "room");
  const onWalls = geometry.some((r) => r.targetKind !== "room");
  const what = onRooms && onWalls ? "rooms, walls and openings" : onRooms ? "rooms" : "walls and openings";
  const count = `${geometry.length} ${geometry.length === 1 ? "correction" : "corrections"} to ${what}`;
  const last = who || date ? ` (last${who ? ` by ${who}` : ""}${date ? ` on ${date}` : ""})` : "";
  return (
    `${count} on the floor plan${last}. These figures were calculated before ` +
    `${geometry.length === 1 ? "it was" : "they were"} made, so they may not match the plan.`
  );
}

/**
 * `planEditedNotice` as the page asks it: against the signature the server
 * says the heat loss is at, and never while a change is still being saved —
 * the answer to that save (a re-conversion, usually) is on its way, and a
 * notice that flashed up for every drag and went away again would be noise.
 */
export function heatLossPlanNotice(
  state: Pick<PlanEditsState, "records" | "saving" | "heatLossSignature">,
): string | null {
  if (state.saving) return null;
  return planEditedNotice(state.records, state.heatLossSignature);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/**
 * "11 Sep 2026" for an ISO timestamp (UTC), or undefined for one that is not
 * a date. Spelled out rather than `toLocaleDateString`, whose short months
 * differ between ICU versions ("Sep" / "Sept").
 */
export function editDate(iso: string): string | undefined {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return undefined;
  return `${when.getUTCDate()} ${MONTHS[when.getUTCMonth()]} ${when.getUTCFullYear()}`;
}

function reasonOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof ApiUnreachableError) return error.message;
  return "Something went wrong saving it.";
}

/** `records` with `batch` laid over them, as the server will hold them once it is saved. */
export function withBatch(
  records: readonly HeldPlanEdit[],
  batch: PlanEditBatch,
  pending = true,
): HeldPlanEdit[] {
  const reverted = new Set(batch.revert.map(planEditKey));
  const byKey = new Map(records.map((r) => [planEditKey(r), r] as const));
  const out = records.filter((r) => !reverted.has(planEditKey(r)));
  const now = new Date().toISOString();
  for (const edit of batch.upsert) {
    const key = planEditKey(edit);
    const held = byKey.get(key);
    const record = {
      ...(held
        ? { id: held.id, surveyId: held.surveyId, createdBy: held.createdBy, createdAt: held.createdAt }
        : { id: `pending-${key}`, surveyId: "", createdBy: null, createdAt: now }),
      ...edit,
      updatedBy: held?.updatedBy ?? null,
      updatedAt: now,
      ...(pending ? { pending: true } : {}),
    } as HeldPlanEdit;
    const index = out.findIndex((r) => planEditKey(r) === key);
    if (index >= 0) out[index] = record;
    else out.push(record);
  }
  return out;
}

/** The fields of a record that make up the correction itself, for sending back. */
export function editOf(record: PlanEdit): PlanEdit {
  const { targetKind, targetId, action, storey } = record;
  return (action === "delete"
    ? { targetKind, targetId, action, storey }
    : { targetKind, targetId, action, storey, data: (record as { data: unknown }).data }) as PlanEdit;
}

function keysOf(batch: PlanEditBatch): string[] {
  return [...batch.upsert.map(planEditKey), ...batch.revert.map(planEditKey)];
}

function openingKindOf(record: { readonly data?: unknown } | null | undefined): unknown {
  const data = record?.data;
  return typeof data === "object" && data !== null ? (data as { kind?: unknown }).kind : undefined;
}

/**
 * What the page says when `batch` was refused because elements of it had
 * moved on (`plan-conflicts.ts`): who changed the first and when, what else
 * in the change was therefore not saved, and how many later changes built on
 * it were not sent.
 */
export function planEditConflictMessage(
  conflicts: readonly PlanEditVersionConflict[],
  batch: PlanEditBatch,
  droppedLater: number,
  opts: ConflictMessageOptions = {},
): string {
  const first = conflicts[0] as PlanEditVersionConflict;
  const sent = batch.upsert.find((e) => planEditKey(e) === planEditKey(first));
  const parts = [conflictMessage(first, editNaming(first.targetKind, openingKindOf(first.current) ?? openingKindOf(sent)), opts)];
  if (conflicts.length > 1) {
    parts.push(`${otherElements(conflicts.slice(1).map((c) => c.targetKind))} in the same change had been changed too.`);
  }
  const conflicted = new Set(conflicts.map(planEditKey));
  const rest = [...batch.upsert, ...batch.revert].filter((t) => !conflicted.has(planEditKey(t)));
  if (rest.length > 0) {
    parts.push(
      `The rest of that change (${otherElements(rest.map((t) => t.targetKind))}) wasn't saved either, so nothing ` +
        `is left half-done — make it again if you still want it.`,
    );
  }
  if (droppedLater > 0) {
    parts.push(
      `${droppedLater} later ${droppedLater === 1 ? "change" : "changes"} you made to it ` +
        `${droppedLater === 1 ? "was" : "were"} not sent.`,
    );
  }
  return parts.join(" ");
}

export interface PlanEditsOptions {
  /** The signed-in user, so a conflict with their own other tab says "You". */
  readonly meId?: string;
}

export function usePlanEdits(
  source: PlanEditsSource | undefined,
  surveyId: string,
  options: PlanEditsOptions = {},
): PlanEditsState {
  const { meId } = options;
  const [saved, setSaved] = useState<readonly PlanEditRecord[]>([]);
  const [inFlight, setInFlight] = useState<readonly { id: number; batch: PlanEditBatch }[]>([]);
  const [status, setStatus] = useState<PlanEditsStatus>(source ? "loading" : "unavailable");
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [heatLossSignature, setHeatLossSignature] = useState(HEAT_LOSS_BASIS_SIGNATURE);
  const [recalculations, setRecalculations] = useState(0);
  const [heatLossNote, setHeatLossNote] = useState<HeatLossWords | null>(null);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const counter = useRef(0);
  const alive = useRef(true);
  // Versions (0046). Refs, not state: the send loop reads them between renders.
  /** Per element, the version this page's picture of it is based on; null: held, but the API sent no version. */
  const basis = useRef(new Map<string, number | null>());
  /** Per element, the version this page's own last save of it left — 0 once it reverted it. */
  const lastOwn = useRef(new Map<string, number>());
  /** Changes queued or on their way, as the send loop sees them (state lags a render). */
  const queued = useRef<{ id: number; batch: PlanEditBatch }[]>([]);
  /** Queued changes built on one the server refused: not to be sent. */
  const dropped = useRef(new Set<number>());
  /** Bumped whenever a save's answer replaces `saved`, so an older re-read never lands over it. */
  const savedGeneration = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * Takes the server's versions as the basis of the page's picture — except
   * for elements a queued change touches: that change was made on the picture
   * as it was, and must be refused if someone has changed them since, not
   * quietly re-based onto their version.
   */
  const adopt = useCallback((edits: readonly PlanEditRecord[]) => {
    const touched = new Set(queued.current.flatMap((q) => keysOf(q.batch)));
    const held = new Map(edits.map((r) => [planEditKey(r), typeof r.version === "number" ? r.version : null] as const));
    for (const key of [...basis.current.keys()]) {
      if (!held.has(key) && !touched.has(key)) basis.current.delete(key);
    }
    for (const [key, version] of held) if (!touched.has(key)) basis.current.set(key, version);
  }, []);

  useEffect(() => {
    if (!source) {
      setStatus("unavailable");
      setSaved([]);
      return;
    }
    let current = true;
    setStatus("loading");
    setLoadError(null);
    // Through a resolved promise, so a source that throws outright (a client
    // without the method) lands in the failure branch, not as an uncaught error.
    Promise.resolve()
      .then(() => source.list())
      .then(
      (list) => {
        if (!current) return;
        if (list === null) {
          setSaved([]);
          setStatus("unsupported");
          return;
        }
        adopt(list.edits);
        setSaved(list.edits);
        setHeatLossSignature(list.heatLossSignature ?? HEAT_LOSS_BASIS_SIGNATURE);
        setStatus("ready");
      },
      (err: unknown) => {
        if (!current) return;
        setLoadError(reasonOf(err));
        setStatus("failed");
      },
    );
    return () => {
      current = false;
    };
  }, [source, surveyId, nonce, adopt]);

  /** Re-reads the list without the "loading" state a reload shows: the plan stays on screen. */
  const refresh = useCallback(() => {
    if (!source) return;
    const generation = savedGeneration.current;
    Promise.resolve()
      .then(() => source.list())
      .then(
        (list) => {
          if (!alive.current || !list || savedGeneration.current !== generation) return;
          adopt(list.edits);
          setSaved(list.edits);
          if (list.heatLossSignature !== undefined) setHeatLossSignature(list.heatLossSignature);
        },
        // The conflict's own rows are already on screen; the next load reads the rest.
        () => undefined,
      );
  }, [source, adopt]);

  const commit = useCallback<PlanEditsState["commit"]>(
    (batch, commitOptions) => {
      if (!source) return Promise.resolve(false);
      counter.current += 1;
      const id = counter.current;
      queued.current = [...queued.current, { id, batch }];
      setInFlight((list) => [...list, { id, batch }]);
      setError(null);
      // A note is about the change it came with; a new change supersedes it.
      setHeatLossNote(null);
      const leave = () => {
        queued.current = queued.current.filter((q) => q.id !== id);
      };
      const run = queue.current.then(() => {
        if (dropped.current.delete(id)) return false;
        // The version of each element this change was made on: what the page
        // last saw of it (0: no correction) — or, for an undo or redo, what
        // this page itself last saved of it.
        const bases = new Map<string, number>();
        for (const key of keysOf(batch)) {
          const own = commitOptions?.replay ? lastOwn.current.get(key) : undefined;
          const version = own ?? (basis.current.has(key) ? basis.current.get(key) : 0);
          // null: the API gave no version (it predates 0046), so none is sent.
          if (typeof version === "number") bases.set(key, version);
        }
        return Promise.resolve()
          .then(() => source.batch(batch, bases))
          .then(
          (list) => {
            leave();
            if (!alive.current) return true;
            // This change's elements are now at the versions its save gave them.
            const answer = new Map(list.edits.map((r) => [planEditKey(r), r] as const));
            for (const key of keysOf(batch)) {
              const record = answer.get(key);
              const version = record ? (typeof record.version === "number" ? record.version : null) : 0;
              basis.current.set(key, version);
              if (version === null) lastOwn.current.delete(key);
              else lastOwn.current.set(key, version);
            }
            adopt(list.edits);
            savedGeneration.current += 1;
            setSaved(list.edits);
            setInFlight((l) => l.filter((b) => b.id !== id));
            if (list.heatLossSignature !== undefined) setHeatLossSignature(list.heatLossSignature);
            if (list.heatLoss?.status === "recalculated") setRecalculations((n) => n + 1);
            const words = heatLossWordsOf(list.heatLoss);
            if (words) setHeatLossNote(words);
            return true;
          },
          (err: unknown) => {
            leave();
            if (!alive.current) return false;
            const conflicts = versionConflicts<PlanEditVersionConflict>(err);
            if (!conflicts) {
              setInFlight((l) => l.filter((b) => b.id !== id));
              setError(`That change to the plan could not be saved, so it has been undone. ${reasonOf(err)}`);
              return false;
            }
            // Someone else got there first. Later queued changes to the same
            // elements were made on top of this one: they are not sent either.
            const hit = new Set(conflicts.map(planEditKey));
            const later = queued.current.filter((q) => keysOf(q.batch).some((key) => hit.has(key)));
            for (const q of later) dropped.current.add(q.id);
            queued.current = queued.current.filter((q) => !dropped.current.has(q.id));
            setInFlight((l) => l.filter((b) => b.id !== id && !later.some((q) => q.id === b.id)));
            // Show THEIR version of what conflicted at once — the server sent
            // it — and base the next change on it; then re-read the rest.
            for (const c of conflicts) {
              const key = planEditKey(c);
              const version = c.current?.version;
              if (c.current === null) basis.current.delete(key);
              else basis.current.set(key, typeof version === "number" ? version : null);
            }
            savedGeneration.current += 1;
            setSaved((now) => {
              const theirs = new Map(
                conflicts.flatMap((c) => (c.current ? [[planEditKey(c), c.current] as const] : [])),
              );
              const kept = now.flatMap((r) => {
                const key = planEditKey(r);
                if (!hit.has(key)) return [r];
                const current = theirs.get(key);
                theirs.delete(key);
                return current ? [current] : [];
              });
              return [...kept, ...theirs.values()];
            });
            setError(planEditConflictMessage(conflicts, batch, later.length, meId ? { meId } : {}));
            refresh();
            return false;
          },
        );
      });
      queue.current = run.catch(() => undefined);
      return run;
    },
    [source, adopt, refresh, meId],
  );

  const records = useMemo(
    () => inFlight.reduce<HeldPlanEdit[]>((acc, { batch }) => withBatch(acc, batch), [...saved]),
    [saved, inFlight],
  );
  const signature = useMemo(() => planEditsSignature(records), [records]);
  const dismissError = useCallback(() => setError(null), []);
  const dismissHeatLossNote = useCallback(() => setHeatLossNote(null), []);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const saving = inFlight.length > 0;

  // One object per actual change, so the page's memos over it (the effective
  // plan, the edit layer) do not recompute on every unrelated render.
  return useMemo(
    () => ({
      status,
      records,
      signature,
      saving,
      heatLossSignature,
      recalculations,
      heatLossNote,
      error,
      loadError,
      commit,
      dismissError,
      dismissHeatLossNote,
      reload,
    }),
    [
      status,
      records,
      signature,
      saving,
      heatLossSignature,
      recalculations,
      heatLossNote,
      error,
      loadError,
      commit,
      dismissError,
      dismissHeatLossNote,
      reload,
    ],
  );
}
